/**
 * decagonBridge — the single postMessage channel from the host page INTO the
 * Decagon widget iframe. Cross-origin by design (the widget is served from
 * decagon.ai), so postMessage is the only path in. Keeping the schema and
 * target origin in ONE module is deliberate: a wrong origin or key name fails
 * silently, so it must not be duplicated at call sites.
 *
 * Documented inbound schema (Decagon):
 *   { addMetadata: {...} }   merge conversation metadata — does NOT advance the turn
 *   { sdkMessage: "..." }    send a message as the user — DOES advance the turn
 */

export const DECAGON_ORIGIN = 'https://decagon.ai'
export const IFRAME_ID = 'decagon-iframe'

const widgetWindow = () => {
  const el = document.getElementById(IFRAME_ID)
  return (el && el.contentWindow) || null
}

/** Merge metadata onto the conversation (no turn). False if no widget mounted. */
export const addMetadata = (metadata) => {
  const w = widgetWindow()
  if (!w) return false
  w.postMessage({ addMetadata: metadata }, DECAGON_ORIGIN)
  return true
}

/** Send a message as the user — advances the conversation to the next turn. */
export const sdkMessage = (text) => {
  const w = widgetWindow()
  if (!w) return false
  w.postMessage({ sdkMessage: text }, DECAGON_ORIGIN)
  return true
}

/**
 * The tokenization round-trip's return leg. Metadata FIRST (the deployed
 * Decagon tool reads payment_nonce & co. as Metadata-source variables), then
 * advance the turn. The message names the card (brand + last-4) when known —
 * the nonce NEVER appears in message text (the transcript is visible in the
 * Decagon dashboard, sims, and exports).
 *
 * Metadata keys are the deployed tool-param contract — do not rename:
 *   payment_nonce, payment_type, payment_last_four, payment_card_type,
 *   billing_first_name, billing_last_name, billing_country, billing_zip_code
 */
export const sendNonce = (nonce, type, billing, card) => {
  const lastFour = (card && card.lastFour) || null
  const cardType = (card && card.cardType) || null
  const okMeta = addMetadata({
    payment_nonce: nonce,
    payment_type: type,
    payment_last_four: lastFour,
    payment_card_type: cardType,
    billing_first_name: (billing && billing.firstName) || null,
    billing_last_name: (billing && billing.lastName) || null,
    billing_country: (billing && billing.country) || null,
    billing_zip_code: (billing && billing.zip) || null,
  })
  const okMsg = sdkMessage(lastFour
    ? (cardType || type) + ' ending in ' + lastFour + ' tokenized'
    : type + ' payment method tokenized')
  return okMeta && okMsg
}

/**
 * The VISIBLE chat-panel rect inside the iframe. Decagon renders the panel
 * inset within the ~500×700 iframe: ~420×640, ~20px margin, anchored
 * bottom-right. It can't be measured (cross-origin), so it is derived from
 * the iframe rect + the widget's known geometry.
 */
export const PANEL_W = 420
export const PANEL_H = 640
export const PANEL_MARGIN = 20

export const widgetPanelRect = () => {
  const el = document.getElementById(IFRAME_ID)
  if (!el) return null
  const r = el.getBoundingClientRect()
  const width = Math.min(PANEL_W, r.width - PANEL_MARGIN)
  const height = Math.min(PANEL_H, r.height - PANEL_MARGIN)
  return {
    top: r.bottom - PANEL_MARGIN - height,
    left: r.right - PANEL_MARGIN - width,
    width: width,
    height: height,
  }
}
